import React, { useState, useEffect, useRef } from 'react';
import { createChart, CandlestickSeries } from 'lightweight-charts';
import { api } from '../lib/api';
import { ChartTradePanel } from '../components/ChartTradePanel';
import { toast } from 'sonner';

const SYMBOL_GROUPS = [
  { label: 'Major FX', symbols: ['EURUSD','GBPUSD','USDJPY','AUDUSD','USDCAD','USDCHF','NZDUSD','EURGBP','EURJPY'] },
  { label: 'Metals',   symbols: ['XAUUSD','XAGUSD'] },
  { label: 'Indices',  symbols: ['US500','US100','US30'] },
  { label: 'Energy',   symbols: ['USOIL','UKOIL'] },
  { label: 'Crypto',   symbols: ['BTCUSD','ETHUSD'] },
];

const TIMEFRAMES = [
  { label: '1m', value: '1m' },
  { label: '5m', value: '5m' },
  { label: '15m', value: '15m' },
  { label: '1H',  value: '1H' },
  { label: '4H',  value: '4H' },
  { label: '1D',  value: '1D' },
];

// Adaptive outputsize: more candles for higher timeframes so chart goes further back
const TF_OUTPUT = { '1m': 300, '5m': 500, '15m': 800, '1H': 1500, '4H': 1000, '1D': 1000 };

const WATCHLIST = ['EURUSD','GBPUSD','XAUUSD','US500','US100','BTCUSD'];

const pc = (v) => v >= 0 ? '#00FF94' : '#FF3333';
const ps = (v) => v >= 0 ? '+' : '';

// ── Price format per instrument ────────────────────────────────────────────
function getPriceFormat(sym) {
  const s = (sym || '').toUpperCase();
  if (s.includes('JPY'))                                       return { precision: 3, minMove: 0.001 };
  if (['XAUUSD','XAGUSD'].includes(s))                        return { precision: 2, minMove: 0.01 };
  if (['US500','US100','US30','USOIL','UKOIL'].includes(s))   return { precision: 2, minMove: 0.01 };
  if (['BTCUSD','ETHUSD'].includes(s))                        return { precision: 2, minMove: 0.01 };
  return { precision: 4, minMove: 0.0001 };  // FX majors — 4-decimal pip precision
}

// ── Futures symbol → chart symbol mapping ─────────────────────────────────
const FUTURES_TO_CHART = {
  MGC: 'XAUUSD', GC:  'XAUUSD',
  SIL: 'XAGUSD', SI:  'XAGUSD',
  MES: 'US500',  ES:  'US500',
  MNQ: 'US100',  NQ:  'US100',
  MYM: 'US30',   YM:  'US30',
  MCL: 'USOIL',  CL:  'USOIL',
  M6E: 'EURUSD', '6E': 'EURUSD',
  M6B: 'GBPUSD', '6B': 'GBPUSD',
  M6J: 'USDJPY', '6J': 'USDJPY',
  M6A: 'AUDUSD', '6A': 'AUDUSD',
};

function stripExpiry(sym) {
  // MGCM26 → MGC,  M6EM26 → M6E,  ESZ25 → ES
  return (sym || '').toUpperCase().replace(/[FGHJKMNQUVXZ]\d{1,2}$/, '');
}

function posToChartSymbol(posSym) {
  const base = stripExpiry(posSym);
  return FUTURES_TO_CHART[base] || posSym.toUpperCase();
}

export default function MarketPage() {
  const [symbol, setSymbol]         = useState('EURUSD');
  const [timeframe, setTimeframe]   = useState('1H');
  const [stratFilter, setStratFilter] = useState('all');
  const [strategies, setStrategies] = useState([]);
  const [livePrice, setLivePrice]   = useState({});
  const [ohlc, setOhlc]             = useState({ o: null, h: null, l: null, c: null });
  const [positions, setPositions]   = useState([]);
  const [totalProfit, setTotalProfit] = useState(0);
  const [pendingArm, setPendingArm] = useState(null);   // {mode, direction, size, sl, tp, override}
  const [chartLoading, setChartLoading] = useState(false);

  // Click anywhere on the chart while a pending order is armed → POST the
  // chart_pending_order at that price. The chart's lightweight-charts
  // priceScale is used to convert pixel → price.
  useEffect(() => {
    if (!pendingArm || !chartRef.current || !seriesRef.current) return;
    const handler = (event) => {
      try {
        const price = seriesRef.current.coordinateToPrice(event.point?.y);
        if (price == null || !Number.isFinite(price)) return;
        api.chartPlacePendingOrder({
          symbol,
          direction:     pendingArm.direction,
          order_type:    pendingArm.mode.toUpperCase(),
          size:          pendingArm.size,
          trigger_price: parseFloat(price.toFixed(5)),
          sl:            pendingArm.sl,
          tp:            pendingArm.tp,
          override_account: pendingArm.override || undefined,
        }).then(() => {
          toast.success(`${pendingArm.mode.toUpperCase()} ${pendingArm.direction} ${symbol} @ ${price.toFixed(5)}`);
          setPendingArm(null);
        }).catch(e => toast.error(e.message));
      } catch (e) { /* swallow */ }
    };
    chartRef.current.subscribeClick(handler);
    return () => { try { chartRef.current?.unsubscribeClick(handler); } catch (_) {} };
  }, [pendingArm, symbol]);                          // eslint-disable-line
  const [symModalOpen, setSymModalOpen] = useState(false);
  const [fearGreed, setFearGreed]   = useState(null);
  const [vix, setVix]               = useState(null);
  const [cotData, setCotData]       = useState([]);
  const [sentiment, setSentiment]   = useState([]);

  const chartContainerRef = useRef(null);
  const chartRef     = useRef(null);
  const seriesRef    = useRef(null);
  const priceLines   = useRef([]);   // track removable price lines
  const loadedRef    = useRef({ symbol: null, timeframe: null });

  // ── Create chart once on mount ─────────────────────────────────────────────
  useEffect(() => {
    if (!chartContainerRef.current) return;
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 370,
      layout: { background: { color: '#0b0e17' }, textColor: '#7a8394' },
      grid: { vertLines: { color: '#131b2e' }, horzLines: { color: '#131b2e' } },
      crosshair: {
        mode: 1,
        vertLine: { color: '#3d4d6b', width: 1, style: 1, labelBackgroundColor: '#1e2d47' },
        horzLine: { color: '#3d4d6b', width: 1, style: 1, labelBackgroundColor: '#1e2d47' },
      },
      rightPriceScale: { borderColor: '#1a2438', textColor: '#7a8394' },
      timeScale: { borderColor: '#1a2438', timeVisible: true, secondsVisible: false, rightOffset: 5 },
      localization: { locale: 'en-US' },
    });

    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#00FF94', downColor: '#FF3333',
      borderUpColor: '#00FF94', borderDownColor: '#FF3333',
      wickUpColor: '#00FF94', wickDownColor: '#FF3333',
      priceFormat: getPriceFormat('EURUSD'),  // default; updated per symbol change
    });

    // OHLC crosshair update
    chart.subscribeCrosshairMove((param) => {
      if (!param.time) return;
      const d = param.seriesData?.get(series);
      if (!d) return;
      setOhlc({ o: d.open, h: d.high, l: d.low, c: d.close });
    });

    chartRef.current  = chart;
    seriesRef.current = series;

    const onResize = () => {
      if (chartContainerRef.current) chart.applyOptions({ width: chartContainerRef.current.clientWidth });
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      try { chart.remove(); } catch (_) {}
      chartRef.current  = null;
      seriesRef.current = null;
      priceLines.current = [];
      loadedRef.current = { symbol: null, timeframe: null };
    };
  }, []);

  // ── Load candles whenever symbol OR timeframe changes ──────────────────────
  useEffect(() => {
    if (!seriesRef.current) return;
    // Avoid double-loading same combination (e.g. on initial mount both effects fire)
    const key = `${symbol}-${timeframe}`;
    if (loadedRef.current.key === key) return;
    loadedRef.current.key = key;

    let cancelled = false;
    setChartLoading(true);

    const outputsize = TF_OUTPUT[timeframe] || 500;
    api.getMarketCandles(symbol, timeframe, outputsize)
      .then(data => {
        if (cancelled || !seriesRef.current) return;
        if (data?.candles?.length) {
          seriesRef.current.setData(data.candles);
          // Default zoom: show the last ~80 candles instead of fitting the
          // full range — gives traders a usable candle size on first load.
          // They can still pan/zoom out manually.
          const ts = chartRef.current?.timeScale();
          if (ts) {
            const total = data.candles.length;
            const start = Math.max(0, total - 80);
            ts.setVisibleLogicalRange({ from: start, to: total + 5 });
          }
          // Set initial OHLC to last candle
          const last = data.candles[data.candles.length - 1];
          setOhlc({ o: last.open, h: last.high, l: last.low, c: last.close });
        }
      })
      .catch(console.error)
      .finally(() => { if (!cancelled) setChartLoading(false); });

    return () => { cancelled = true; };
  }, [symbol, timeframe]);

  // ── Update price format when symbol changes ────────────────────────────────
  useEffect(() => {
    if (!seriesRef.current) return;
    seriesRef.current.applyOptions({ priceFormat: getPriceFormat(symbol) });
  }, [symbol]);
  useEffect(() => {
    const load = async () => {
      const [strats, fg, vixData, cot, sent, pos, personal] = await Promise.all([
        api.getStrategies().catch(() => []),
        api.getFearGreed().catch(() => null),
        api.getVIX().catch(() => null),
        api.getCOT().catch(() => []),
        api.getSentiment().catch(() => []),
        api.getMasterPositions().catch(() => ({ positions: [], total_profit: 0 })),
        api.getPersonalStrategy().catch(() => null),
      ]);
      // Personal strategy (self-as-master) is prepended so it's the
      // first chip after "All Strategies". This is the ONLY strategy
      // that unlocks the manual trade panel below the chart.
      const activePublic = (strats || []).filter(s => s.status === 'active');
      // Sort user-created strategies (is_my_strategy) to the very front so
      // they always appear immediately after "All / Personal" filter chips.
      activePublic.sort((a, b) => {
        const am = a.is_my_strategy ? 0 : 1;
        const bm = b.is_my_strategy ? 0 : 1;
        return am - bm;
      });
      const combined = personal ? [personal, ...activePublic] : activePublic;
      setStrategies(combined);
      setFearGreed(fg);
      setVix(vixData);
      setCotData(cot || []);
      setSentiment(sent || []);
      setPositions(pos?.positions || []);
      setTotalProfit(Number(pos?.total_profit || 0));
    };
    load();
  }, []);

  // ── Fetch quote for active symbol ──────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    api.getMarketQuote(symbol)
      .then(q => {
        if (!cancelled && q?.price)
          setLivePrice(prev => ({ ...prev, [symbol]: { price: Number(q.price), pct: Number(q.percent_change || 0) } }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [symbol]);

  // ── Overlay position price lines when stratFilter or symbol changes ────────
  useEffect(() => {
    if (!seriesRef.current) return;

    // Remove all existing price lines
    priceLines.current.forEach(line => {
      try { seriesRef.current.removePriceLine(line); } catch (_) {}
    });
    priceLines.current = [];
    try { seriesRef.current.setMarkers([]); } catch (_) {}

    if (!positions.length) return;

    // Filter positions by strategy
    const filtered = stratFilter === 'all'
      ? positions
      : positions.filter(p => p.strategy_id === stratFilter);

    if (!filtered.length) return;

    // Convert each position's symbol to a chart-compatible symbol and match to current chart
    const forChart = filtered.filter(p => posToChartSymbol(p.symbol) === symbol.toUpperCase());

    // If selected strategy has positions but on a different symbol → auto-switch
    if (forChart.length === 0 && stratFilter !== 'all' && filtered.length > 0) {
      const targetSym = posToChartSymbol(filtered[0].symbol);
      if (targetSym !== symbol.toUpperCase()) {
        loadedRef.current.key = null;
        setSymbol(targetSym);
        return; // will re-run after symbol state update
      }
    }

    const newLines = [];
    const markers  = [];
    const lastTs   = Math.floor(Date.now() / 1000);

    forChart.forEach(pos => {
      const isBuy  = (pos.type || '').toUpperCase().includes('BUY');
      const colour = isBuy ? '#00FF94' : '#FF3333';
      const label  = `${isBuy ? 'BUY' : 'SELL'} ${pos.volume || ''}`.trim();

      // Entry price line — solid, coloured
      if (pos.open_price != null) {
        try {
          const line = seriesRef.current.createPriceLine({
            price:            parseFloat(pos.open_price),
            color:            colour,
            lineWidth:        2,
            lineStyle:        0,   // 0 = Solid
            axisLabelVisible: true,
            title:            label,
          });
          newLines.push(line);
        } catch (_) {}
      }

      // Stop loss — red dotted
      if (pos.sl != null) {
        try {
          const line = seriesRef.current.createPriceLine({
            price:            parseFloat(pos.sl),
            color:            '#FF3333',
            lineWidth:        1,
            lineStyle:        1,   // 1 = Dotted
            axisLabelVisible: true,
            title:            'SL',
          });
          newLines.push(line);
        } catch (_) {}
      }

      // Take profit — green dotted
      if (pos.tp != null) {
        try {
          const line = seriesRef.current.createPriceLine({
            price:            parseFloat(pos.tp),
            color:            '#00FF94',
            lineWidth:        1,
            lineStyle:        1,
            axisLabelVisible: true,
            title:            'TP',
          });
          newLines.push(line);
        } catch (_) {}
      }

      // Arrow marker at the latest bar
      markers.push({
        time:     lastTs,
        position: isBuy ? 'belowBar' : 'aboveBar',
        color:    colour,
        shape:    isBuy ? 'arrowUp' : 'arrowDown',
        text:     label,
        size:     1,
      });
    });

    priceLines.current = newLines;
    try { seriesRef.current.setMarkers(markers); } catch (_) {}

  }, [positions, symbol, stratFilter]); // eslint-disable-line

  const handleSymbol = (s) => {
    loadedRef.current.key = null; // force reload for new symbol
    setSymbol(s);
    setSymModalOpen(false);
  };

  const handleTimeframe = (tf) => {
    loadedRef.current.key = null; // force reload for new timeframe
    setTimeframe(tf);
  };

  const symbolPrice = livePrice[symbol];
  const fgColor = fearGreed
    ? (fearGreed.value <= 25 ? '#FF3333' : fearGreed.value >= 75 ? '#00FF94' : '#FFB000')
    : '#A1A1AA';

  const fmtPrice = (v) => {
    if (v == null) return '—';
    return v > 999 ? v.toFixed(2) : v.toFixed(5);
  };

  return (
    <div className="page-container">
      {/* Watchlist ticker */}
      <div style={{ overflowX: 'auto', borderBottom: '1px solid var(--pp-border)', background: '#0b0e17', flexShrink: 0 }}>
        <div data-testid="market-ticker-bar" style={{ display: 'flex', padding: '6px 10px', gap: 10, minWidth: 'max-content' }}>
          {WATCHLIST.map(sym => {
            const q = livePrice[sym];
            const isActive = sym === symbol;
            return (
              <button key={sym} onClick={() => handleSymbol(sym)}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4px 10px',
                  background: isActive ? '#1a2438' : 'transparent',
                  border: `1px solid ${isActive ? '#2d3f5e' : 'transparent'}`,
                  cursor: 'pointer' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: isActive ? '#fff' : '#7a8394', letterSpacing: '0.05em' }}>{sym}</span>
                {q && <>
                  <span className="mono" style={{ fontSize: 11, color: isActive ? '#FFD700' : '#e0e0e0', fontWeight: 600 }}>
                    {fmtPrice(q.price)}
                  </span>
                  <span className="mono" style={{ fontSize: 9, color: pc(q.pct) }}>{ps(q.pct)}{q.pct.toFixed(2)}%</span>
                </>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Symbol + price bar */}
      <div style={{ background: '#0b0e17', borderBottom: '1px solid var(--pp-border)', padding: '8px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <button data-testid="market-symbol-select" onClick={() => setSymModalOpen(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#131b2e',
              border: '1px solid #1e2d47', padding: '7px 14px', cursor: 'pointer' }}>
            <span className="mono" style={{ color: '#fff', fontSize: 15, fontWeight: 800 }}>{symbol}</span>
            <span style={{ color: '#666', fontSize: 12 }}>▼</span>
          </button>
          {symbolPrice && (
            <div style={{ display: 'flex', flex: 1, justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span className="mono" style={{ color: '#FFD700', fontSize: 20, fontWeight: 800 }}>
                  {fmtPrice(symbolPrice.price)}
                </span>
                <span className="mono" style={{ fontSize: 11, color: pc(symbolPrice.pct), marginLeft: 10 }}>
                  {ps(symbolPrice.pct)}{symbolPrice.pct.toFixed(3)}%
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#0d2d1a',
                border: '1px solid rgba(0,255,148,0.2)', padding: '4px 10px' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00FF94', animation: 'pulse-glow 2s infinite' }} />
                <span style={{ color: '#00FF94', fontSize: 9, fontWeight: 800 }}>LIVE</span>
              </div>
            </div>
          )}
        </div>

        {/* Timeframe selector */}
        <div data-testid="market-timeframe-selector" style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
          {TIMEFRAMES.map(tf => (
            <button key={tf.value} data-testid={`market-timeframe-${tf.value}`}
              onClick={() => handleTimeframe(tf.value)}
              style={{ flex: 1, padding: '7px 0', background: timeframe === tf.value ? '#9B2335' : '#131b2e',
                border: 'none', color: timeframe === tf.value ? '#fff' : '#7a8394',
                fontSize: 12, fontWeight: timeframe === tf.value ? 700 : 400, cursor: 'pointer' }}>
              {tf.label}
            </button>
          ))}
        </div>

        {/* Strategy overlay filter */}
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
          <button onClick={() => setStratFilter('all')}
            style={{ flexShrink: 0, padding: '5px 14px', background: stratFilter === 'all' ? '#9B2335' : '#131b2e',
              border: stratFilter === 'all' ? '1px solid #9B2335' : '1px solid #1e2d47',
              color: '#fff', fontSize: 11, fontWeight: stratFilter === 'all' ? 700 : 400, cursor: 'pointer' }}>
            All Strategies
          </button>
          {/* MY STRATEGIES tab: only shown when the user actually has personal
              strategies (self-as-master OR user-created). Selecting it filters
              the chart overlay to the trader's own master accounts. */}
          {strategies.some(s => s.is_my_strategy || s.is_personal) && (
            <button data-testid="strat-filter-mine" onClick={() => setStratFilter('mine')}
              style={{ flexShrink: 0, padding: '5px 14px',
                background: stratFilter === 'mine' ? 'rgba(0,255,148,0.18)' : '#131b2e',
                border: stratFilter === 'mine' ? '1px solid #00FF94' : '1px solid #1e2d47',
                color: stratFilter === 'mine' ? '#00FF94' : '#8be8b6',
                fontSize: 11, fontWeight: stratFilter === 'mine' ? 700 : 400,
                cursor: 'pointer', letterSpacing: '0.04em' }}>
              ★ My Strategies
            </button>
          )}
          {strategies
            .filter(s => stratFilter === 'mine' ? (s.is_my_strategy || s.is_personal) : true)
            .map(s => {
            const isActive = stratFilter === s.strategy_id;
            const isPersonal = !!s.is_personal;
            const isMine = !!s.is_my_strategy;
            // Personal & mine chips use neon green to signal "this unlocks manual trading".
            const ownGreen = (isPersonal || isMine);
            const accent = ownGreen ? '#00FF94' : '#2979FF';
            const bgActive = ownGreen ? 'rgba(0,255,148,0.15)' : '#2979FF22';
            return (
              <button key={s.strategy_id} data-testid={`strat-chip-${s.strategy_id}`} onClick={() => setStratFilter(s.strategy_id)}
                style={{ flexShrink: 0, padding: '5px 14px', background: isActive ? bgActive : '#131b2e',
                  border: `1px solid ${isActive ? accent : '#1e2d47'}`,
                  color: isActive ? accent : (ownGreen ? '#8be8b6' : '#7a8394'),
                  fontSize: 11, fontWeight: isActive ? 700 : 400, cursor: 'pointer',
                  letterSpacing: ownGreen ? '0.04em' : 'normal' }}>
                {ownGreen ? `★ ${s.name}` : s.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* OHLC display */}
      <div style={{ background: '#0b0e17', padding: '5px 12px', display: 'flex', gap: 14, borderBottom: '1px solid #131b2e' }}>
        {[
          { label: 'O', value: fmtPrice(ohlc.o), color: '#e0e0e0' },
          { label: 'H', value: fmtPrice(ohlc.h), color: '#00FF94' },
          { label: 'L', value: fmtPrice(ohlc.l), color: '#FF3333' },
          { label: 'C', value: fmtPrice(ohlc.c), color: ohlc.c != null && ohlc.o != null ? (ohlc.c >= ohlc.o ? '#00FF94' : '#FF3333') : '#e0e0e0' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 9, color: '#444', letterSpacing: '1px', fontWeight: 700 }}>{label}</span>
            <span className="mono" style={{ fontSize: 12, fontWeight: 700, color }}>{value}</span>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div data-testid="market-candlestick-chart" style={{ position: 'relative', background: '#0b0e17' }}>
        {chartLoading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', background: '#0b0e17', zIndex: 10, gap: 12 }}>
            <div style={{ width: 28, height: 28, border: '2px solid #F59E0B', borderTopColor: 'transparent',
              borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            <span style={{ color: '#7a8394', fontSize: 13 }}>Loading {symbol} {timeframe}…</span>
          </div>
        )}
        <div ref={chartContainerRef} style={{ width: '100%', minHeight: 370 }} />
      </div>

      {/* On-chart trading widget — appears when the trader has selected
          their own Personal Strategy (self-as-master) OR a user-created
          strategy they own. Public/automated strategies stay view-only. */}
      {(() => {
        const selected = strategies.find(s => s.strategy_id === stratFilter);
        const canTrade = !!(selected && (selected.is_personal || selected.is_my_strategy));
        if (!canTrade) {
          return (
            <div data-testid="chart-trade-gated" style={{ borderTop: '1px solid var(--pp-border)', background: '#0a0e18', padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#FFB000', flexShrink: 0 }} />
              <p style={{ margin: 0, fontSize: 12, color: 'var(--pp-text-3)', lineHeight: 1.5 }}>
                {(stratFilter === 'all' || stratFilter === 'mine')
                  ? <>Manual trading is locked. Select one of your <strong style={{ color: '#00FF94' }}>★ My Strategies</strong> chips above to enable Buy / Sell from this chart. Public automated strategies are view-only — they fire copy trades from their master account, not yours.</>
                  : <>This is a community strategy — manual orders are disabled here so your trade history stays clean. Switch to one of your <strong style={{ color: '#00FF94' }}>★ My Strategies</strong> to trade manually.</>
                }
              </p>
            </div>
          );
        }
        // Forced target: when the user picked one of THEIR user-created
        // strategies, force the trade panel to fire on that strategy's master
        // account (so manual orders land on the same broker the strategy
        // broadcasts from). Personal (self-as-master) keeps default primary.
        const forced = selected.is_my_strategy && selected.master_account ? selected.master_account : null;
        return (
          <div style={{ borderTop: '1px solid var(--pp-border)', background: '#0a0e18', padding: 12 }}>
            {forced && (
              <div data-testid="chart-trade-forced-target" style={{ marginBottom: 8, padding: '6px 10px', background: 'rgba(0,255,148,0.08)', border: '1px solid rgba(0,255,148,0.3)', fontSize: 11, color: '#8be8b6' }}>
                Trading on the <strong style={{ color: '#00FF94' }}>{forced.label}</strong> ({forced.platform_kind}) master account for <strong style={{ color: '#fff' }}>{selected.name}</strong>.
              </div>
            )}
            <ChartTradePanel symbol={symbol} lastPrice={livePrice?.[symbol] || ohlc?.c} onArmPending={setPendingArm} forcedAccount={forced} />
            {pendingArm && (
              <div data-testid="pending-arm-banner" style={{ marginTop: 8, padding: 10, background: 'rgba(255,176,0,0.08)', border: '1px solid rgba(255,176,0,0.4)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                <span style={{ color: '#FFB000', fontWeight: 700 }}>
                  ⚡ {pendingArm.mode.toUpperCase()} {pendingArm.direction} {pendingArm.size} armed — click the chart at your trigger price
                </span>
                <button onClick={() => setPendingArm(null)} style={{ background: 'none', border: '1px solid var(--pp-border)', color: 'var(--pp-text-3)', padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>× CANCEL</button>
              </div>
            )}
          </div>
        );
      })()}

      {/* Master Positions */}
      <div style={{ borderTop: '1px solid var(--pp-border)', background: '#0d1220' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 14px', borderBottom: '1px solid var(--pp-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--pp-primary)' }} />
            <span style={{ color: '#7a8394', fontSize: 10, fontWeight: 700, letterSpacing: '0.15em' }}>
              MASTER POSITIONS ({positions.length})
            </span>
          </div>
          {positions.length > 0 &&
            <span className="mono" style={{ fontSize: 12, fontWeight: 700, color: pc(totalProfit) }}>
              {ps(totalProfit)}${totalProfit.toFixed(2)} P&L
            </span>}
        </div>
        {positions.length === 0 ? (
          <div style={{ padding: '20px 14px', textAlign: 'center' }}>
            <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.3 }}>〰️</div>
            <span style={{ color: '#333', fontSize: 12 }}>No open positions on master account</span>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 550 }}>
              <thead>
                <tr>{['Symbol','Type','Vol','Entry','SL','TP','P&L'].map(h => (
                  <th key={h} style={{ color: '#444', fontSize: 9, fontWeight: 700, padding: '6px 10px',
                    textAlign: 'center', borderBottom: '1px solid #131b2e', letterSpacing: '0.1em' }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {positions.map((p, i) => {
                  const isBuy = (p.type || '').toUpperCase().includes('BUY');
                  const pnl   = Number(p.profit || 0);
                  return (
                    <tr key={p.id || i} style={{ background: i % 2 ? '#0c1118' : 'transparent' }}>
                      <td className="mono" style={{ fontSize: 11, padding: '6px 10px', textAlign: 'center', color: '#e0e0e0' }}>{p.symbol}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                        <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 6px',
                          color: isBuy ? '#22c55e' : '#ef4444', background: isBuy ? '#22c55e22' : '#ef444422' }}>
                          {isBuy ? 'BUY' : 'SELL'}
                        </span>
                      </td>
                      <td className="mono" style={{ fontSize: 10, padding: '6px 10px', textAlign: 'center', color: '#9B9B9B' }}>{p.volume}</td>
                      <td className="mono" style={{ fontSize: 10, padding: '6px 10px', textAlign: 'center', color: '#9B9B9B' }}>{fmtPrice(p.open_price)}</td>
                      <td className="mono" style={{ fontSize: 10, padding: '6px 10px', textAlign: 'center', color: '#ef4444' }}>{fmtPrice(p.sl)}</td>
                      <td className="mono" style={{ fontSize: 10, padding: '6px 10px', textAlign: 'center', color: '#22c55e' }}>{fmtPrice(p.tp)}</td>
                      <td className="mono" style={{ fontSize: 11, padding: '6px 10px', textAlign: 'center', fontWeight: 700, color: pc(pnl) }}>
                        {ps(pnl)}${pnl.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Market Pulse */}
      <div style={{ padding: '14px 14px 0' }}>
        <p className="section-label" style={{ marginBottom: 10 }}>Market Pulse</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          {/* Fear & Greed */}
          <div style={{ background: '#0d1220', border: '1px solid #131b2e', padding: 14, textAlign: 'center' }}>
            <p className="section-label" style={{ marginBottom: 6 }}>Fear & Greed Index</p>
            <p className="mono" style={{ fontSize: 32, fontWeight: 800, color: fgColor, margin: '6px 0' }}>{fearGreed?.value ?? '—'}</p>
            <p style={{ fontSize: 9, color: fgColor, fontWeight: 700, letterSpacing: '0.1em', marginBottom: 10 }}>{(fearGreed?.classification || '').toUpperCase()}</p>
            {fearGreed?.value != null && (
              <>
                <div style={{ height: 4, background: 'linear-gradient(to right, #FF3333, #FFB000, #00FF94)', position: 'relative', marginBottom: 4 }}>
                  <div style={{ position: 'absolute', top: -3, left: `${fearGreed.value}%`, width: 2, height: 10, background: '#fff', transform: 'translateX(-50%)' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 8, color: '#FF3333', fontWeight: 700 }}>FEAR</span>
                  <span style={{ fontSize: 8, color: '#A1A1AA' }}>NEUTRAL</span>
                  <span style={{ fontSize: 8, color: '#00FF94', fontWeight: 700 }}>GREED</span>
                </div>
              </>
            )}
          </div>
          {/* VIX */}
          <div style={{ background: '#0d1220', border: '1px solid #131b2e', padding: 14, textAlign: 'center' }}>
            <p className="section-label" style={{ marginBottom: 6 }}>VIX Volatility</p>
            <p className="mono" style={{ fontSize: 32, fontWeight: 800, margin: '6px 0',
              color: vix?.value > 25 ? '#FF3333' : vix?.value > 15 ? '#FFB000' : '#00FF94' }}>
              {vix?.value?.toFixed(1) ?? '—'}
            </p>
            <p className="mono" style={{ fontSize: 9, color: (vix?.change || 0) >= 0 ? '#FF3333' : '#00FF94', fontWeight: 700, marginBottom: 8 }}>
              {(vix?.change || 0) >= 0 ? '+' : ''}{vix?.change?.toFixed(2) || '0'}%
            </p>
            <p style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
              color: vix?.value > 30 ? '#FF3333' : vix?.value > 20 ? '#FFB000' : '#00FF94' }}>
              {vix?.value > 30 ? 'HIGH FEAR / VOLATILE' : vix?.value > 20 ? 'ELEVATED RISK' : '✅ LOW VOLATILITY'}
            </p>
          </div>
        </div>

        {/* Retail Sentiment */}
        {sentiment.length > 0 && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <p className="section-label">Retail Sentiment</p>
              <p style={{ fontSize: 9, color: '#3d4d6b', fontStyle: 'italic' }}>{sentiment.length} pairs · Myfxbook</p>
            </div>
            {sentiment.slice(0, 10).map((item, i) => {
              const lp = Number(item.long_pct || 50);
              const sp = Number(item.short_pct || 50);
              const biasLabel = lp > 65 ? 'BEARISH EDGE' : sp > 65 ? 'BULLISH EDGE' : 'NEUTRAL';
              const biasColor = lp > 65 ? '#FF3333' : sp > 65 ? '#00FF94' : '#7a8394';
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #131b2e', gap: 10 }}>
                  <div style={{ width: 72, flexShrink: 0 }}>
                    <p className="mono" style={{ margin: 0, fontSize: 12, fontWeight: 800, color: '#e0e0e0' }}>{item.pair}</p>
                    {item.traders_count && <p style={{ margin: 0, fontSize: 9, color: '#3d4d6b' }}>{(item.traders_count/1000).toFixed(1)}K traders</p>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', marginBottom: 3 }}>
                      <div style={{ flex: lp, background: '#22c55e' }} />
                      <div style={{ flex: sp, background: '#ef4444' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span className="mono" style={{ fontSize: 9, color: '#22c55e', fontWeight: 700 }}>{lp}% Long</span>
                      <span className="mono" style={{ fontSize: 9, color: '#ef4444', fontWeight: 700 }}>{sp}% Short</span>
                    </div>
                  </div>
                  <div style={{ width: 88, textAlign: 'right' }}>
                    <span style={{ fontSize: 9, fontWeight: 700, color: biasColor }}>⚖ {biasLabel}</span>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* COT Reports */}
        {cotData.length > 0 && (
          <>
            <p className="section-label" style={{ margin: '16px 0 8px' }}>COT Reports — Institutional Positioning</p>
            {cotData.map((item, i) => {
              const net = Number(item.non_commercial_net || 0);
              const netPos = net >= 0;
              return (
                <div key={i} style={{ background: '#0d1220', border: '1px solid #131b2e', marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: 12 }}>
                    <div>
                      <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#e0e0e0' }}>{item.currency} — {item.pair}</p>
                      <p style={{ margin: '2px 0 0', fontSize: 10, color: '#3d4d6b' }}>{item.date} · {item.source}</p>
                    </div>
                    <div style={{ background: (netPos ? '#22c55e' : '#ef4444') + '20', padding: '5px 10px' }}>
                      <span className="mono" style={{ fontSize: 12, fontWeight: 800, color: netPos ? '#22c55e' : '#ef4444' }}>
                        {netPos ? '▲' : '▼'} {Math.abs(net).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div style={{ padding: '0 12px 12px' }}>
                    <p style={{ margin: '0 0 6px', fontSize: 8, fontWeight: 700, letterSpacing: '0.15em', color: '#3d4d6b' }}>
                      NON-COMMERCIAL (SPECULATORS)
                    </p>
                    <div style={{ display: 'flex', gap: 20 }}>
                      {[
                        { v: (item.non_commercial_long || 0).toLocaleString(), l: 'LONG', c: '#22c55e' },
                        { v: (item.non_commercial_short || 0).toLocaleString(), l: 'SHORT', c: '#ef4444' },
                        { v: (net >= 0 ? '+' : '') + net.toLocaleString(), l: 'NET', c: netPos ? '#22c55e' : '#ef4444' },
                      ].map(m => (
                        <div key={m.l} style={{ textAlign: 'center' }}>
                          <p className="mono" style={{ margin: 0, fontSize: 14, fontWeight: 700, color: m.c }}>{m.v}</p>
                          <p style={{ margin: 0, fontSize: 9, color: '#3d4d6b', letterSpacing: '0.1em' }}>{m.l}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ borderTop: '1px solid #131b2e', padding: '8px 12px' }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: netPos ? '#22c55e' : '#ef4444' }}>
                      {netPos ? 'Institutions NET LONG — potential bullish bias' : 'Institutions NET SHORT — potential bearish bias'}
                    </span>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Symbol modal */}
      {symModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--pp-bg)', zIndex: 200, overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottom: '1px solid var(--pp-border)' }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Select Instrument</h2>
            <button onClick={() => setSymModalOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--pp-text)', cursor: 'pointer', fontSize: 24, padding: 4 }}>×</button>
          </div>
          {SYMBOL_GROUPS.map(group => (
            <div key={group.label} style={{ padding: '14px 16px 6px' }}>
              <p className="section-label" style={{ marginBottom: 8 }}>{group.label}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {group.symbols.map(s => (
                  <button key={s} onClick={() => handleSymbol(s)}
                    style={{ padding: '9px 14px', background: symbol === s ? 'var(--pp-primary)' : 'var(--pp-surface)',
                      border: '1px solid var(--pp-border)', color: symbol === s ? '#fff' : 'var(--pp-text-3)',
                      fontSize: 13, cursor: 'pointer', fontFamily: 'monospace', fontWeight: symbol === s ? 700 : 400 }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div style={{ height: 40 }} />
        </div>
      )}
      <div style={{ height: 20 }} />
    </div>
  );
}
